import Spacer from '@/common/components/spacer';
import { cn } from '@/shadcn-ui/utils';
import CreditAgeChangeDrawerDialog, {
  CreditAgeChangeDrawerDialogProps,
} from '../../credit-age-change-drawer-dialog';
import PDPPlanSelectionHelperDrawerDialog from '../../pdp-plan-selection-helper-drawer-dialog';
import PlanRadioGroup from '../plan-radio-group/plan-radio-group';
import { VehiclePlan } from '@/catalog/types';
import useMileagePlanCopy from '@/catalog/hooks/use-mileage-plan-copy';
import usePlanGuidePopover, {
  PlanGuidePopoverConfig,
} from '@/catalog/hooks/use-plan-guide-popover';
import GuidePopover from '@/common/components/guide-popover';

export type CreditConfig = Omit<
  CreditAgeChangeDrawerDialogProps,
  'error' | 'className' | 'dismissible'
>;

export interface MileagePlanSelectionProps {
  // Mileage plans
  vehiclePlans: VehiclePlan[];
  selectedMileagePlanId?: string;
  mostPopularPlanMileageAmount?: string;
  splitPayment?: boolean;
  handleSelectMilagePlan?: (value: string) => void;

  // Additional config
  errorConfig: PlanGuidePopoverConfig;
  inSwapFlow?: boolean;
  isOrderable: boolean;
  price_savings_1200_over_850?: number;
  price_per_mile_850_plan_for_1200_miles?: number;
  price_difference_850_vs_1200?: number;
  creditConfig: CreditConfig;
  popoverTriggerRef?: React.RefObject<HTMLDivElement | null>;
  hasNoSelectedAgeCreditRange: boolean;
}

export default function MileagePlanSelection({
  // Mileage plans
  vehiclePlans,
  selectedMileagePlanId,
  mostPopularPlanMileageAmount,
  splitPayment,
  errorConfig,
  handleSelectMilagePlan,

  // Additional config
  inSwapFlow,
  isOrderable,
  price_savings_1200_over_850,
  price_per_mile_850_plan_for_1200_miles,
  price_difference_850_vs_1200,
  creditConfig,
  popoverTriggerRef,
  hasNoSelectedAgeCreditRange,
}: MileagePlanSelectionProps) {
  const notOrderable = !isOrderable;
  const disableMileagePlanSelection = notOrderable || hasNoSelectedAgeCreditRange;
  const { showPopover, error } = usePlanGuidePopover({ errorConfig });
  const { mileagePlansCopy, zeroMilePlanId } = useMileagePlanCopy({
    plans: vehiclePlans,
    mostPopularPlanMileageAmount,
    splitPayment,
    disableMileagePlanSelection,
    inSwapFlow,
  });

  /** Wrapper func to prevent users from being able to select PAYG plan while on split payment */
  const onValueChangeMileagePlan = (value: string) => {
    if (value === String(zeroMilePlanId) && splitPayment) {
      return;
    }

    handleSelectMilagePlan?.(value);
  };

  const handleClick = () => {
    // When mileage buttons are disabled due to no credit age range selection, clicking them should open the credit age change drawer
    if (!hasNoSelectedAgeCreditRange) {
      return;
    }

    creditConfig.handleOpen();
  };

  return (
    <>
      {/* Mileage plan selection header */}
      <div className="flex flex-col gap-[4px]">
        <GuidePopover
          show={showPopover}
          triggerComponent={
            <div
              className="flex items-center justify-between gap-[8px]"
              ref={popoverTriggerRef}
            >
              <h3
                className={cn('font-sans-tight', {
                  'text-flexcar-neutral70': disableMileagePlanSelection,
                })}
              >
                {inSwapFlow ? 'Your' : 'Monthly'} mileage plan
              </h3>

              {/* Plan Selection Helper */}
              <PDPPlanSelectionHelperDrawerDialog
                className={cn({ 'opacity-40': disableMileagePlanSelection })}
                vehiclePlans={vehiclePlans}
                price_savings_1200_over_850={price_savings_1200_over_850}
                price_per_mile_850_plan_for_1200_miles={
                  price_per_mile_850_plan_for_1200_miles
                }
                price_difference_850_vs_1200={price_difference_850_vs_1200}
              />
            </div>
          }
          content={error}
        />

        {!inSwapFlow ? (
          <>
            <p
              className={cn('body2-tight', {
                'text-flexcar-neutral70': disableMileagePlanSelection,
              })}
            >
              Miles roll over. Change plans anytime.
            </p>

            <CreditAgeChangeDrawerDialog
              open={creditConfig.open}
              handleOpen={creditConfig.handleOpen}
              handleClose={creditConfig.handleClose}
              selectedCreditScoreId={creditConfig.selectedCreditScoreId}
              selectedIsOver25={creditConfig.selectedIsOver25}
              selectedAgeRangeId={creditConfig.selectedAgeRangeId}
              creditScores={creditConfig.creditScores}
              ageRanges={creditConfig.ageRanges}
              className={cn('w-fit', {
                'opacity-40': notOrderable,
              })}
              creditAgeSummaryClassName={cn({
                'text-flexcar-blue40 decoration-flexcar-blue40 hover:text-flexcar-blue60 hover:decoration-flexcar-blue60':
                  hasNoSelectedAgeCreditRange,
              })}
              key={`credit-age-drawer-dialog-${creditConfig.selectedCreditScoreId}-${creditConfig.selectedIsOver25}-${creditConfig.selectedAgeRangeId}`}
            />
          </>
        ) : null}
      </div>

      <Spacer variant="height16" />

      {/* Mileage plan radio group */}
      <PlanRadioGroup
        plans={mileagePlansCopy}
        name="plan"
        value={selectedMileagePlanId}
        selectedPlanId={selectedMileagePlanId}
        disabled={disableMileagePlanSelection}
        onValueChange={onValueChangeMileagePlan}
        onClickDelegate={handleClick}
        planRadioButtonClassName="cursor-pointer"
      />
    </>
  );
}
